import { useState, useEffect } from 'react'
import { Copy, Check, RefreshCw } from 'lucide-react'

interface ProfileData {
  email: string
  name: string
  authenticated: boolean
  bearer_token?: string
}

function decodeTokenExpiry(token: string): Date | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp ? new Date(payload.exp * 1000) : null
  } catch {
    return null
  }
}

function formatExpiry(date: Date): string {
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()
  if (diffMs <= 0) return 'Expired'
  const hours = Math.floor(diffMs / 3600000)
  const mins = Math.floor((diffMs % 3600000) / 60000)
  return `${date.toLocaleString()} (${hours}h ${mins}m remaining)`
}

export function Profile() {
  const [profile, setProfile] = useState<ProfileData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [regenerating, setRegenerating] = useState(false)

  const API_URL = import.meta.env.VITE_API_URL ?? ''

  useEffect(() => {
    fetchProfile()
  }, [])

  async function fetchProfile() {
    try {
      const res = await fetch(`${API_URL}/api/v1/auth/me`, { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch profile')
      const data = await res.json()
      setProfile(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  async function regenerateToken() {
    setRegenerating(true)
    try {
      const res = await fetch(`${API_URL}/api/v1/auth/regenerate`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to regenerate token')
      const data = await res.json()
      setProfile(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Regeneration failed')
    } finally {
      setRegenerating(false)
    }
  }

  function copyToken() {
    if (profile?.bearer_token) {
      navigator.clipboard.writeText(profile.bearer_token)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  if (loading) {
    return <div style={{ padding: 'var(--space-2)', color: 'var(--text-muted)' }}>Loading…</div>
  }

  if (error || !profile) {
    return <div style={{ padding: 'var(--space-2)', color: 'var(--text-error)' }}>Error: {error}</div>
  }

  return (
    <div style={{ padding: 'var(--space-2)' }}>
      <h1 style={{ fontSize: 'var(--text-h2-size)', marginBottom: 'var(--space-2)' }}>Profile</h1>

      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-2)',
        marginBottom: 'var(--space-2)',
      }}>
        <div style={{ marginBottom: 'var(--space-15)' }}>
          <div style={{ fontSize: 'var(--text-body3-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-05)' }}>
            Name
          </div>
          <div style={{ fontSize: 'var(--text-body1-size)' }}>{profile.name}</div>
        </div>

        <div style={{ marginBottom: 'var(--space-15)' }}>
          <div style={{ fontSize: 'var(--text-body3-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-05)' }}>
            Email
          </div>
          <div style={{ fontSize: 'var(--text-body1-size)' }}>{profile.email}</div>
        </div>

        <div>
          <div style={{ fontSize: 'var(--text-body3-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-05)' }}>
            Platform Access Token
          </div>
          <div style={{
            display: 'flex',
            gap: 'var(--space-1)',
            alignItems: 'center',
            background: 'var(--bg-default)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--space-1)',
            fontFamily: 'monospace',
            fontSize: 'var(--text-body3-size)',
            wordBreak: 'break-all',
          }}>
            <span style={{ flex: 1, color: 'var(--text-muted)' }}>
              {profile.bearer_token ? (
                <>
                  {profile.bearer_token.substring(0, 12)}
                  <span style={{ color: 'var(--text-secondary)', letterSpacing: '2px' }}>{' ••••••••••• '}</span>
                  {profile.bearer_token.substring(profile.bearer_token.length - 8)}
                </>
              ) : (
                'No token available'
              )}
            </span>
            <button
              onClick={copyToken}
              title="Copy full token"
              disabled={!profile.bearer_token}
              style={{
                background: copied ? 'var(--bg-success)' : 'var(--bg-brand)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-xs)',
                padding: 'var(--space-05) var(--space-1)',
                cursor: profile.bearer_token ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-05)',
                fontSize: 'var(--text-body3-size)',
                opacity: profile.bearer_token ? 1 : 0.5,
              }}
            >
              {copied ? (
                <>
                  <Check size={12} /> Copied
                </>
              ) : (
                <>
                  <Copy size={12} /> Copy
                </>
              )}
            </button>
            <button
              onClick={regenerateToken}
              title="Regenerate token"
              disabled={regenerating}
              style={{
                background: 'var(--bg-subtle)',
                color: 'var(--text-default)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-xs)',
                padding: 'var(--space-05) var(--space-1)',
                cursor: regenerating ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-05)',
                fontSize: 'var(--text-body3-size)',
                opacity: regenerating ? 0.5 : 1,
              }}
            >
              <RefreshCw size={12} style={regenerating ? { animation: 'spin 1s linear infinite' } : undefined} /> Regenerate
            </button>
          </div>
          {profile.bearer_token && (() => {
            const expiry = decodeTokenExpiry(profile.bearer_token)
            if (!expiry) return null
            const isExpired = expiry.getTime() <= Date.now()
            return (
              <div style={{
                marginTop: 'var(--space-05)',
                fontSize: 'var(--text-body3-size)',
                color: isExpired ? 'var(--text-error)' : 'var(--text-muted)',
              }}>
                {isExpired ? '⚠ Token expired — click Regenerate' : `Expires: ${formatExpiry(expiry)}`}
              </div>
            )
          })()}
        </div>
      </div>

      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-info)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-1)',
        color: 'var(--text-muted)',
        fontSize: 'var(--text-body3-size)',
        lineHeight: 1.5,
      }}>
        <strong>How to use your token:</strong>
        <ol style={{ marginLeft: 'var(--space-15)', marginTop: 'var(--space-05)' }}>
          <li>Copy the token above.</li>
          <li>Your repo's <code style={{ background: 'var(--bg-default)', padding: '2px 4px', borderRadius: '2px' }}>.mcp.json</code> already has <code style={{ background: 'var(--bg-default)', padding: '2px 4px', borderRadius: '2px' }}>{'${input:sdlcToken}'}</code> — the IDE will prompt you for this token on first use.</li>
          <li>Paste your token when prompted. The IDE stores it securely and sends it on every MCP request.</li>
        </ol>
      </div>
    </div>
  )
}
